// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useMemo } from 'react';

import type { Task, WorkOrderStateName, WithOptional } from '@evolvemep/api-ts';
import { Popover, Stack, Divider, InputLabel } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNotNil, isNil } from 'helpers/isNotNil';
import { useTaskStatuses } from 'hooks/typeCacheHooks/useTaskStatuses';
import { TaskPredecessorsList } from 'modules/Shop/Tasks/TaskPredecessorsList';
import { BadgeButton } from 'modules/Shop/WorkOrders/WorkOrderDetail/BadgeButton';
import { hasEverBeenReleased } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';

type Props = {
  workOrderState?: WorkOrderStateName;
  task: WithOptional<Pick<Task, 'taskId' | 'taskPredecessors' | 'hasIncompletePredecessors'>, 'taskPredecessors'>;
};

export const PredecessorsBadge = ({ workOrderState = 'In Progress', task }: Props) => {
  const { states: taskStatusTypes } = useTaskStatuses();
  const [opened, setOpened] = useState(false);

  const released = useMemo(() => hasEverBeenReleased(workOrderState), [workOrderState]);

  const completeTasks = useMemo(() => {
    if (!released) return null;
    const completedStatus = taskStatusTypes?.find((s) => s.name === 'Completed')?.stateId;
    if (!completedStatus) return null;
    return task.taskPredecessors?.filter((t) => t.taskStatusTypeId === completedStatus).length;
  }, [task.taskPredecessors, taskStatusTypes, released]);

  if (task.taskPredecessors?.length === 0 || (isNil(task.taskPredecessors) && !task.hasIncompletePredecessors)) {
    return null;
  }

  return (
    <Popover opened={opened} onChange={setOpened} withArrow position="top">
      <Popover.Target>
        <AgGridStyleTooltip
          label={`${released && task.hasIncompletePredecessors ? 'This task has incomplete predecessors. ' : ''}Click to see all predecessors.`}
          hidden={opened}
          withArrow
        >
          {isNotNil(task.taskPredecessors) ? (
            <BadgeButton icon="Blocked" onClick={() => setOpened((o) => !o)} size="xl">
              {isNotNil(completeTasks)
                ? `${completeTasks}/${task.taskPredecessors.length}`
                : task.taskPredecessors.length}
            </BadgeButton>
          ) : (
            <BadgeButton
              onClick={() => setOpened((o) => !o)}
              variant="light"
              radius="sm"
              size="lg"
              w="var(--badge-height)"
              pl={0}
              pr={0}
            >
              <EvolveIcon icon="Blocked" size="sm" />
            </BadgeButton>
          )}
        </AgGridStyleTooltip>
      </Popover.Target>
      <Popover.Dropdown
        p={0}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <Stack gap="xs" ta="left" py="xs" miw={250}>
          <InputLabel px="sm">Predecessors</InputLabel>
          <Divider />
          <Stack px="sm" gap={8}>
            <TaskPredecessorsList task={task} />
          </Stack>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
